import React, { useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import {
  getAdminSocietyDetail,
  getFlatMembers,
  flatMemberRemovalCheck,
  removeFlatMember,
  FlatMemberInfo,
  AdminSocietyDetail as AdminSocietyDetailData,
  AdminSocietyFlat,
  RemovalCheckData,
} from '../services/api';
import './AdminSocietyFlatMembers.css';

type RoleCategory = 'owner' | 'co_owner' | 'tenant' | 'family';

function normalizeRoleCategory(roleInFlat: string): RoleCategory {
  const r = roleInFlat.toLowerCase().replace(/[\s-]+/g, '_');
  if (r === 'co_owner') return 'co_owner';
  if (r === 'owner') return 'owner';
  if (r.includes('family')) return 'family';
  return 'tenant';
}

function roleBadgeLabel(category: RoleCategory): string {
  if (category === 'owner') return 'Owner';
  if (category === 'co_owner') return 'Co-owner';
  if (category === 'family') return 'Family Member';
  return 'Tenant';
}

function countByRole(members: FlatMemberInfo[]): { owner: number; coOwner: number; tenant: number; family: number } {
  return members.reduce(
    (acc, m) => {
      const c = normalizeRoleCategory(m.roleInFlat);
      if (c === 'owner') acc.owner += 1;
      else if (c === 'co_owner') acc.coOwner += 1;
      else if (c === 'family') acc.family += 1;
      else acc.tenant += 1;
      return acc;
    },
    { owner: 0, coOwner: 0, tenant: 0, family: 0 }
  );
}

function formatJoined(iso: string): string {
  try {
    return new Date(iso).toLocaleDateString(undefined, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  } catch {
    return iso;
  }
}

function formatRupees(amount: number): string {
  return `₹${amount.toLocaleString('en-IN', { minimumFractionDigits: 0, maximumFractionDigits: 2 })}`;
}

function buildConfirmMessage(check: RemovalCheckData): string {
  const pending = check.pendingBillCount > 0;
  const cascade = check.cascadeMemberCount > 0;
  const billPart = pending
    ? `⚠ ${check.name} has ${check.pendingBillCount} pending bill(s) totalling ${formatRupees(check.pendingBillTotal)}. These bills will remain unpaid.`
    : '';
  const cascadePart = cascade
    ? `${check.name} and ${check.cascadeMemberCount} linked member(s) will also be removed.`
    : '';
  const normal = `Remove ${check.name} (${check.roleInFlat}) from flat ${check.flatNumber}?`;

  if (pending && cascade) {
    return `${billPart}\n\n${cascadePart}\n\nContinue?`;
  }
  if (pending) {
    return `${billPart}\n\nContinue?`;
  }
  if (cascade) {
    return `${cascadePart}\n\nContinue?`;
  }
  return normal;
}

const AdminSocietyFlatMembers: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const societyId = id ? parseInt(id, 10) : NaN;

  const [detail, setDetail] = useState<AdminSocietyDetailData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const [expandedFlatIds, setExpandedFlatIds] = useState<Set<number>>(() => new Set());
  const [membersLoadingIds, setMembersLoadingIds] = useState<Set<number>>(() => new Set());
  const [membersErrorByFlat, setMembersErrorByFlat] = useState<Record<number, string>>({});
  const [membersCache, setMembersCache] = useState<Record<number, FlatMemberInfo[]>>({});
  const [removingKey, setRemovingKey] = useState<string | null>(null);

  useEffect(() => {
    if (isNaN(societyId)) {
      setError('Invalid society');
      setLoading(false);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        setLoading(true);
        setError('');
        const res = await getAdminSocietyDetail(societyId);
        if (!cancelled) setDetail(res.data);
      } catch (e: unknown) {
        if (!cancelled) {
          setError(e instanceof Error ? e.message : 'Failed to load society');
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [societyId]);

  const displayTotalMembers = useMemo(() => {
    if (!detail?.flats) return 0;
    return detail.flats.reduce((sum, f) => {
      const cached = membersCache[f.id];
      if (cached !== undefined) return sum + cached.length;
      return sum + (f.users?.length ?? 0);
    }, 0);
  }, [detail, membersCache]);

  const ensureMembersLoaded = async (flat: AdminSocietyFlat) => {
    if (membersCache[flat.id] !== undefined) return;
    setMembersLoadingIds((prev) => new Set(prev).add(flat.id));
    setMembersErrorByFlat((prev) => {
      const next = { ...prev };
      delete next[flat.id];
      return next;
    });
    try {
      const res = await getFlatMembers(flat.id);
      setMembersCache((prev) => ({ ...prev, [flat.id]: res.data }));
    } catch (e: unknown) {
      setMembersErrorByFlat((prev) => ({
        ...prev,
        [flat.id]: e instanceof Error ? e.message : 'Failed to load members',
      }));
    } finally {
      setMembersLoadingIds((prev) => {
        const next = new Set(prev);
        next.delete(flat.id);
        return next;
      });
    }
  };

  const refreshFlatMembers = async (flatId: number) => {
    setMembersErrorByFlat((prev) => {
      const next = { ...prev };
      delete next[flatId];
      return next;
    });
    try {
      const res = await getFlatMembers(flatId);
      setMembersCache((prev) => ({ ...prev, [flatId]: res.data }));
    } catch (e: unknown) {
      setMembersErrorByFlat((prev) => ({
        ...prev,
        [flatId]: e instanceof Error ? e.message : 'Failed to refresh members',
      }));
    }
  };

  const toggleFlat = (flat: AdminSocietyFlat) => {
    setExpandedFlatIds((prev) => {
      const next = new Set(prev);
      if (next.has(flat.id)) {
        next.delete(flat.id);
      } else {
        next.add(flat.id);
        void ensureMembersLoaded(flat);
      }
      return next;
    });
  };

  const onRemoveClick = async (flat: AdminSocietyFlat, member: FlatMemberInfo) => {
    const key = `${flat.id}-${member.userId}`;
    if (removingKey) return;
    setRemovingKey(key);
    try {
      const res = await flatMemberRemovalCheck(flat.id, member.userId);
      const check = res.data;
      const msg = buildConfirmMessage(check);
      if (!window.confirm(msg)) return;
      const removeRes = await removeFlatMember(flat.id, member.userId);
      if (!removeRes.success) {
        window.alert(removeRes.message || 'Removal failed');
        return;
      }
      await refreshFlatMembers(flat.id);
    } catch (e: unknown) {
      window.alert(e instanceof Error ? e.message : 'Could not remove member');
    } finally {
      setRemovingKey(null);
    }
  };

  if (loading) {
    return <div className="admin-flat-members-loading">Loading society…</div>;
  }

  if (error || !detail) {
    return (
      <div className="admin-flat-members">
        <Link to={isNaN(societyId) ? '/admin/societies' : `/admin/societies/${societyId}`} className="admin-flat-members-back">
          ← Back to society
        </Link>
        <div className="admin-flat-members-error">{error || 'Not found'}</div>
      </div>
    );
  }

  const { society, flats } = detail;
  const societyBack = `/admin/societies/${society.id}`;

  return (
    <div className="admin-flat-members">
      <Link to={societyBack} className="admin-flat-members-back">
        ← Back to society
      </Link>

      <header className="admin-flat-members-header">
        <div className="admin-flat-members-title-row">
          <h1 className="admin-flat-members-title">Flat Members</h1>
          <span className="admin-flat-members-total-badge" title="Total members (all flats)">
            {displayTotalMembers} members
          </span>
        </div>
        <p className="admin-flat-members-society-name">{society.name}</p>
      </header>

      <div className="admin-flat-members-list">
        {flats.length === 0 ? (
          <div className="admin-flat-members-empty">No flats in this society.</div>
        ) : (
          flats.map((flat) => {
            const expanded = expandedFlatIds.has(flat.id);
            const members = membersCache[flat.id];
            const counts =
              members !== undefined ? countByRole(members) : { owner: 0, coOwner: 0, tenant: 0, family: 0 };
            const occupied = (flat.users?.length ?? 0) > 0;
            const membersLoading = membersLoadingIds.has(flat.id);
            const membersErr = membersErrorByFlat[flat.id];
            const flatLabel = [flat.block_name, flat.flat_number].filter(Boolean).join(' · ') || flat.flat_number;

            const nameByUserId = new Map<number, string>();
            if (members) {
              for (const m of members) {
                nameByUserId.set(m.userId, m.name);
              }
            }

            return (
              <article
                key={flat.id}
                className={`admin-flat-members-card ${expanded ? 'admin-flat-members-card--expanded' : ''}`}
              >
                <button
                  type="button"
                  className="admin-flat-members-card-header"
                  onClick={() => toggleFlat(flat)}
                  aria-expanded={expanded}
                >
                  <span
                    className="admin-flat-members-chevron"
                    aria-hidden
                  />
                  <div className="admin-flat-members-card-main">
                    <div className="admin-flat-members-card-title-row">
                      <span className="admin-flat-members-flat-label">Flat {flatLabel}</span>
                      <span
                        className={`admin-flat-members-occ-badge ${
                          occupied ? 'admin-flat-members-occ-badge--yes' : 'admin-flat-members-occ-badge--no'
                        }`}
                      >
                        {occupied ? 'Occupied' : 'Vacant'}
                      </span>
                    </div>
                    <div className="admin-flat-members-card-meta">
                      {flat.block_name != null && flat.block_name !== '' && (
                        <span>Block {flat.block_name}</span>
                      )}
                      <span>Floor {flat.floor}</span>
                      {flat.area != null && <span>{flat.area} sq units</span>}
                    </div>
                    <div className="admin-flat-members-summary-badges">
                      {members === undefined && !expanded ? (
                        <span className="admin-flat-members-count-hint">Expand for role breakdown</span>
                      ) : membersLoading && members === undefined ? (
                        <span className="admin-flat-members-count-hint">Loading counts…</span>
                      ) : (
                        <>
                          <span className="admin-flat-members-mini-badge admin-flat-members-mini-badge--owner">
                            {counts.owner} Owner{counts.owner !== 1 ? 's' : ''}
                          </span>
                          {counts.coOwner > 0 && (
                            <span className="admin-flat-members-mini-badge admin-flat-members-mini-badge--co-owner">
                              {counts.coOwner} Co-owner{counts.coOwner !== 1 ? 's' : ''}
                            </span>
                          )}
                          <span className="admin-flat-members-mini-badge admin-flat-members-mini-badge--tenant">
                            {counts.tenant} Tenant{counts.tenant !== 1 ? 's' : ''}
                          </span>
                          <span className="admin-flat-members-mini-badge admin-flat-members-mini-badge--family">
                            {counts.family} {counts.family === 1 ? 'Family' : 'Families'}
                          </span>
                        </>
                      )}
                    </div>
                  </div>
                </button>

                <div className="admin-flat-members-card-panel">
                  <div className="admin-flat-members-card-panel-inner">
                    {membersErr && (
                      <div className="admin-flat-members-inline-error" role="alert">
                        {membersErr}
                      </div>
                    )}
                    {membersLoading && members === undefined && !membersErr && (
                      <div className="admin-flat-members-panel-loading">Loading members…</div>
                    )}
                    {members && (
                      <div className="admin-flat-members-table-wrap">
                        <table className="admin-flat-members-table">
                          <thead>
                            <tr>
                              <th>Name</th>
                              <th>Phone</th>
                              <th>Role</th>
                              <th>Linked To</th>
                              <th>Status</th>
                              <th>Joined</th>
                              <th className="admin-flat-members-th-actions">Actions</th>
                            </tr>
                          </thead>
                          <tbody>
                            {members.length === 0 ? (
                              <tr>
                                <td colSpan={7} className="admin-flat-members-table-empty">
                                  No members linked to this flat.
                                </td>
                              </tr>
                            ) : (
                              members.map((m) => {
                                const cat = normalizeRoleCategory(m.roleInFlat);
                                const linkedName =
                                  m.linkedToUserId != null
                                    ? nameByUserId.get(m.linkedToUserId) ?? '—'
                                    : '—';
                                const statusLabel = m.isApproved ? 'Approved' : 'Pending';
                                const removeDisabled = removingKey === `${flat.id}-${m.userId}`;
                                return (
                                  <tr key={m.userFlatId}>
                                    <td className="admin-flat-members-td-name">{m.name}</td>
                                    <td>{m.phone || '—'}</td>
                                    <td>
                                      <span
                                        className={`admin-flat-members-role admin-flat-members-role--${cat}`}
                                      >
                                        {roleBadgeLabel(cat)}
                                      </span>
                                    </td>
                                    <td>{linkedName}</td>
                                    <td>
                                      <span
                                        className={
                                          m.isApproved
                                            ? 'admin-flat-members-status-ok'
                                            : 'admin-flat-members-status-pending'
                                        }
                                      >
                                        {statusLabel}
                                      </span>
                                    </td>
                                    <td>{formatJoined(m.joinedAt)}</td>
                                    <td>
                                      <button
                                        type="button"
                                        className="admin-flat-members-remove"
                                        disabled={removeDisabled}
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          void onRemoveClick(flat, m);
                                        }}
                                      >
                                        {removeDisabled ? '…' : 'Remove'}
                                      </button>
                                    </td>
                                  </tr>
                                );
                              })
                            )}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </div>
                </div>
              </article>
            );
          })
        )}
      </div>
    </div>
  );
};

export default AdminSocietyFlatMembers;
